import React, { useEffect, useRef, useState } from 'react';
import { CONNECTOR_HOVER_DELAY, connectorSegments, connectorSegmentPath } from '../../utils/mindmap/connectorEditing';

export default function ConnectorHandles({ route, scale = 1, onDrag, onReset }) {
  const [hovered, setHovered] = useState(null);
  const [dragging, setDragging] = useState(false);
  const timer = useRef(null);
  const gesture = useRef(null);
  const hitRef = useRef(null);
  const callbacks = useRef({ onDrag, onReset });
  callbacks.current = { onDrag, onReset };
  const zoom = Math.max(0.05, scale);
  const clearTimer = () => { clearTimeout(timer.current); timer.current = null; };
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (gesture.current) callbacks.current.onDrag?.('cancel');
  }, []);
  useEffect(() => {
    const cancel = event => {
      if (event.key === 'Escape' && gesture.current) {
        event.preventDefault(); event.stopPropagation();
        callbacks.current.onDrag?.('cancel');
        const pointerId = gesture.current.pointerId;
        gesture.current = null; setDragging(false); setHovered(null);
        hitRef.current?.releasePointerCapture?.(pointerId);
      }
    };
    window.addEventListener('keydown', cancel, true);
    return () => window.removeEventListener('keydown', cancel, true);
  }, []);
  const segments = connectorSegments(route);
  const active = gesture.current;
  const segment = active
    ? segments.find(entry => entry.axis === active.axis && Math.abs(entry.start[entry.axis] - active.coordinate - active.delta) < 0.01)
    : segments.find(entry => entry.index === hovered);
  const highlightPath = segment ? connectorSegmentPath(route, segment.index, 3 / zoom) : '';
  const begin = (event, entry) => {
    if (event.button !== 0 || gesture.current) return;
    event.preventDefault(); event.stopPropagation(); clearTimer();
    gesture.current = { pointerId: event.pointerId, index: entry.index, axis: entry.axis, coordinate: entry.start[entry.axis], delta: 0, x: event.clientX, y: event.clientY, zoom };
    hitRef.current = event.currentTarget;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setHovered(entry.index); setDragging(true);
    callbacks.current.onDrag?.('start', entry.index);
  };
  const move = event => {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault(); event.stopPropagation();
    active.delta = (active.axis === 'x' ? event.clientX - active.x : event.clientY - active.y) / active.zoom;
    callbacks.current.onDrag?.('move', active.index, active.delta);
  };
  const finish = (event, cancelled = false) => {
    if (!gesture.current || gesture.current.pointerId !== event.pointerId) return;
    event.preventDefault(); event.stopPropagation();
    if (!cancelled) move(event);
    gesture.current = null; setDragging(false);
    callbacks.current.onDrag?.(cancelled ? 'cancel' : 'end');
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const reset = event => { event.preventDefault(); event.stopPropagation(); callbacks.current.onReset?.(); };
  return <g data-export-ignore="true" data-html2canvas-ignore="true" className="connector-handles" onMouseDown={event => event.stopPropagation()} data-connector-dragging={dragging ? 'true' : 'false'}>
    {highlightPath && <g style={{ pointerEvents: 'none' }}>
      <path data-connector-highlight="true" d={highlightPath} fill="none" stroke="#1d4ed8" strokeWidth={2.5 / zoom} strokeLinecap="butt" />
    </g>}
    {segments.map(entry => <path
      key={entry.index}
      data-connector-segment={entry.index}
      data-connector-axis={entry.axis}
      tabIndex="0" role="slider"
      aria-label={entry.axis === 'x' ? 'Move vertical connector sideways' : 'Move horizontal connector up or down'}
      aria-orientation={entry.axis === 'x' ? 'horizontal' : 'vertical'}
      aria-valuenow={Math.round(entry.start[entry.axis])}
      onFocus={() => { clearTimer(); setHovered(entry.index); }}
      onBlur={() => { if (!gesture.current) setHovered(null); }}
      onKeyDown={event => {
        const negative = entry.axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
        const positive = entry.axis === 'x' ? 'ArrowRight' : 'ArrowDown';
        if (event.key === negative || event.key === positive) {
          event.preventDefault(); event.stopPropagation();
          callbacks.current.onDrag?.('start', entry.index);
          callbacks.current.onDrag?.('move', entry.index, (event.key === negative ? -1 : 1) * (event.shiftKey ? 20 : 4));
          callbacks.current.onDrag?.('end');
        } else if (event.key === 'Delete' || event.key === 'Backspace') reset(event);
      }}
      d={connectorSegmentPath(route, entry.index)}
      fill="none" stroke="transparent" strokeWidth={20 / zoom}
      style={{ pointerEvents: 'stroke', touchAction: 'none', cursor: hovered === entry.index ? (entry.axis === 'x' ? 'ew-resize' : 'ns-resize') : 'default' }}
      onPointerEnter={() => { if (gesture.current) return; clearTimer(); timer.current = setTimeout(() => setHovered(entry.index), CONNECTOR_HOVER_DELAY); }}
      onPointerLeave={() => { if (gesture.current) return; clearTimer(); timer.current = setTimeout(() => setHovered(null), 120); }}
      onPointerDown={event => begin(event, entry)} onPointerMove={move}
      onPointerUp={event => finish(event)} onPointerCancel={event => finish(event, true)}
      onLostPointerCapture={event => finish(event, true)}
      onClick={event => event.stopPropagation()} onDoubleClick={reset}
    />)}

  </g>;
}
